import { useAppContext } from '@aspendental/shared-components-web';
import { getFacilityDetailsUrl } from '@aspendental/shared-components-web/lib/components/GoogleMap/GoogleMap.helpers';

import {
	getFacilityDefaultImage,
	getFacilitySelectedImage,
} from '@aspendental/shared-components-web/lib/components/GoogleMap/StandardGoogleMap/StandardGoogleMap.helpers';

import LocationCard from '@aspendental/shared-components-web/lib/components/GoogleMap/StandardGoogleMap/LocationCard/LocationCard';

import { Box, Grid, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material';
import dynamic from 'next/dynamic';

import { IProviderDetailsPage } from './ProviderDetailsPage';
import { getProviderOfficesTitle } from './ProviderDetailsPage.helpers';
import {
	getLocationsCardContainerStyles,
	getMapAndLocationsComponentStyles,
	getProviderContentContainerStyles,
	providerOfficesTitleStyles,
} from './ProviderDetailsPage.styles';

const ProviderGoogleMap = dynamic(() => import('./ProviderGoogleMap'), {
	ssr: false,
	loading: () => <Skeleton variant="rounded" height="100%" width="100%" />,
});

import { NearbyFacility } from '@src/lib/__generated/sdk';

export default function ProviderOffices({ provider, config, googleMapsData }: Readonly<IProviderDetailsPage>) {
	const theme = useTheme();
	const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'));
	const facilitiesArr = (provider.providerLocations || []).filter(({ facility }) => facility);
	const currentFacility = facilitiesArr[0] || {};
	const currentFacilityLat = currentFacility.facility?.location?.latitude;
	const currentFacilityLon = currentFacility.facility?.location?.longitude;

	const { environment } = useAppContext();
	const googleMapId =
		environment === 'prod' ? config.services.prod.GOOGLE_MAP_ID : config.services.nonprod.GOOGLE_MAP_ID;
	const googleMapApiKey =
		environment === 'prod' ? config.services.prod.GOOGLE_MAP_API_KEY : config.services.nonprod.GOOGLE_MAP_API_KEY;
	const facilityBasePath = config.features.otherApps.facilityDetailsBasePath;
	const showSetOfficeButtonInGoogleMapsCards = config.featureFlags.showSetOfficeButtonInGoogleMapsCards;

	const handleMarkerClick = (facilityUrl: string) => {
		const officeDetailsUrl = getFacilityDetailsUrl(facilityBasePath, facilityUrl);
		if (globalThis?.location) {
			globalThis.location.href = officeDetailsUrl;
		}
	};

	return (
		<Box data-testid="provider_offices_container" sx={getProviderContentContainerStyles}>
			<Box data-testid="provider_offices_content_container">
				<Typography
					//@ts-ignore
					variant="header1"
					color="text.primary"
					component="h2"
					data-testid="provider_offices_title"
					sx={{
						...providerOfficesTitleStyles,
						...{ textTransform: config.name === 'ClearChoice' ? 'capitalize' : 'none' },
					}}
				>
					{getProviderOfficesTitle(provider)}
				</Typography>
			</Box>
			<Grid item container xs={12}>
				{/* entire Map and Locations container */}
				<Grid
					container
					direction={isMobileScreen ? 'column-reverse' : 'row'}
					sx={getMapAndLocationsComponentStyles(isMobileScreen)}
				>
					{/* Locations cards container item */}
					<Grid
						data-testid="provider_offices_locations_cards"
						item
						container
						direction={isMobileScreen ? 'column' : 'row'}
						md={5}
						sx={getLocationsCardContainerStyles(theme, isMobileScreen)}
					>
						{/* Location card */}
						{facilitiesArr.map(({ facility }, index: number) => {
							return (
								<LocationCard
									key={index}
									index={index}
									isSmallScreen={isMobileScreen}
									//@ts-ignore
									location={facility as NearbyFacility['facility']}
									distance={0}
									defaultImage={getFacilityDefaultImage(facility?.brand)}
									selectedImage={getFacilitySelectedImage(facility?.brand)}
									facilityBasePath={facilityBasePath}
									googleMapsBusinessQuery={config.googleMapBusinessQuery}
									facilityBadgeLabel={config.googleMapOfficeBadgeLabel}
									facilityButtonLabel={config.googleMapMapOfficeButtonLabel}
									showSetOfficeButtonInGoogleMapsCards={showSetOfficeButtonInGoogleMapsCards}
									setAsOfficeButtonText={googleMapsData?.setAsOfficeButtonText}
								/>
							);
						})}
					</Grid>
					{/* maps item */}
					<Grid item md={7} data-testid="provider_offices_google_map">
						<ProviderGoogleMap
							facilitiesArr={facilitiesArr}
							currentFacilityLat={currentFacilityLat}
							currentFacilityLon={currentFacilityLon}
							isMobileScreen={isMobileScreen}
							handleMarkerClick={handleMarkerClick}
							googleMapId={googleMapId}
							googleMapApiKey={googleMapApiKey}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Box>
	);
}
